import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../api";
import {
  Query,
  Result,
  QueryState,
  InitialQueryState,
} from "../interfaces/queryBuilderInterfaces";
import { moveMeasuresToLast } from "../utils/util";
import { setMapData } from "./mapSlice";
import { RootState } from "./store";
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getNextTabName = (queries: any, isReset: boolean, currentIndex: number | null = null) => {
  let index = 1;

  const existingNames = queries.map((q: any) => q.tabName);

  if(existingNames.length === 1 && isReset) {
    return `Tab ${index}`;
  } 

  while (existingNames.includes(`Tab ${index}`)) {
    if(currentIndex !== null && existingNames[currentIndex] === `Tab ${index}` && isReset) {
      break;
    }
    index++;
  }

  return `Tab ${index}`;
};

export const newQuery = createAsyncThunk<
  Query,
  { formData: FormData; index: null | number }
>("olapquery/new", async ({ formData, index }) => {
  const response = await axiosInstance.post(`/rest/olapquery/new`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  /*if (response.data.name) {
            const payload = response.data;
            const setupResp = await axiosInstance.post(`/rest/olapquery/${payload.name}}/setup`, payload);
            return setupResp.data;
        }*/
  return response.data;
});

export const setupQuery = createAsyncThunk<
  Query,
  { payload: Query; index: null | number }
>("olapquery/setup", async ({ payload, index }) => {
  payload = moveMeasuresToLast(payload);

  if (payload.filterLevel) {
    const filterLevel = payload.filterLevel;
    delete payload.filterLevel;
    const response = await axiosInstance.post(
      `/rest/olapquery/${payload.name}/setup?filterlevel=${filterLevel}`,
      payload,
    );
    return response.data;
  }
  const response = await axiosInstance.post(
    `/rest/olapquery/${payload.name}/setup`,
    payload,
  );
  return response.data;
});

export const resultQuery = createAsyncThunk<
  Result,
  {
    query: Query;
    queryMode?: string;
    querySubType?: string;
    index?: null | number;
  }
>(
  "olapquery/result",
  async ({ query: payload, queryMode, querySubType, index }) => {
    let response = await axiosInstance.get(
      queryMode && queryMode === "Viz"
        ? `/rest/olapquery/${payload.name}/result/chart/${querySubType}`
        : `/rest/olapquery/${payload.name}/result/flattened`,
    );
    if (response.data.result.resultFill === "TimedOut") {
      // Call this API until the status is 'Completed'
      do {
        await delay(1000); // Wait for 1 second
        response = await axiosInstance.get(
          `/rest/olapquery/result/checkstatus/${response.data.uid}`,
        );
      } while (response.data.result.resultFill === "TimedOut");
    }
    return response.data;
  },
);

export const historyQuery = createAsyncThunk<
  Result,
  { index: number; queryName: string; currentTabIndex: null | number }
>(
  "olapquery/history",
  async ({ index, queryName, currentTabIndex }, thunkAPI) => {
    let state = thunkAPI.getState() as RootState;
    let response = await axiosInstance.get(
      `/rest/olapquery/${queryName}/history/${index}`,
    );
    if (response.data && response.data.activeQueryDetail?.queryMode == "Map") {
      thunkAPI.dispatch(
        setMapData({ data: response.data, currentTabIndex: currentTabIndex }),
      );
    }
    return response.data;
  },
);

export const groupFnQuery = createAsyncThunk<
  Result,
  { query: Query; flag: string; fn: string; index: null | number }
>("olapquery/groupFn", async ({ query, flag, fn, index }) => {
  const response = await axiosInstance.get(
    `/rest/olapquery/${query.name}/groupFn/overall/${flag}/${fn}`,
  );
  return response.data;
});

export const selectCurrentTab = (state: any) =>
  state.queryBuilder.queries[state.queryBuilder.currentTabIndex];

export const currActiveTab = (state: any) => state.queryBuilder.currentTabIndex;

export const tabsData = (state: any) =>
  state.queryBuilder.queries.map((tab: any) => {
    return {
      tabName: tab.tabName,
      tabOrder: tab.tabOrder,
      queryName: tab.query?.name || "",
      queryMode: tab.queryMode,
      querySubType: tab.querySubType,
      msrSelections: tab.msrSelections || null,
      gridSorts: tab.query?.gridSorts || null,
    };
  });

const singleQueryState: QueryState = {
  query: null,
  results: null,
  loading: true,
  error: null,
  queryMode: "Grid",
  querySubType: null,

  msrSelections: null,
  isQueryUpdated: false,
  isNewQuery: false,
  resetGrpFn: false,
  axesWithAllSelected: [],
  isUpdatedTab: false,
  currentQueryIndex: 0,
  historyCount: 0,
  isSavingQuery: false,
  currentQueryType: "Grid",
  isSourceHistory: false,
  isLoadQueryMode: true,
  tabName: "Tab 1",
  tabOrder: 1,
  isSavedQueryLoaded: true,
  isLoadingQuery: false,
  updatedNamedsetList: {}
};

const initialState: InitialQueryState = {
  queries: [{ ...singleQueryState }],
  currentTabIndex: 0,
  loadQueryIds: [],
  isLoadingSavedQueries: false,
  currUpdatingTabIndex: 0,
};

// const getCurrentQuery = (state: any) => state.queries[state.currentTabIndex];
const getCurrentQuery = (state: any, index?: null | number) =>
  index != null
    ? state.queries[index]
    : state.queries[
        state.isLoadingSavedQueries
          ? state.currUpdatingTabIndex
          : state.currentTabIndex
      ];

const queryBuilderSlice = createSlice({
  name: "queryBuilder",
  initialState,
  reducers: {
    setCurrentQueryIndex(
      state,
      action: PayloadAction<{ activeIndex: number; currentTabIndex: number }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).currentQueryIndex =
        action.payload?.activeIndex;
    },
    setHistoryCount(
      state,
      action: PayloadAction<{ historyLength: number; currentTabIndex: number }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).historyCount =
        action.payload.historyLength;
    },
    setLoadQueryMode(
      state,
      action: PayloadAction<{ flag: boolean; currentTabIndex: number }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).isLoadQueryMode =
        action.payload.flag;
    },
    setSourceFlag(
      state,
      action: PayloadAction<{ flag: boolean; currentTabIndex: number }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).isSourceHistory =
        action.payload.flag;
    },
    setCurrentQueryType(
      state,
      action: PayloadAction<{ type: string; currentTabIndex: number }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).currentQueryType =
        action.payload.type;
    },
    setQuery(state, action: PayloadAction<any>) {
      getCurrentQuery(state, action.payload.currentTabIndex).query =
        action.payload.query;
    },
    setAxesWithAllSelected(state, action: PayloadAction<any>) {
      getCurrentQuery(
        state,
        action.payload.currentTabIndex,
      ).axesWithAllSelected = action.payload.axes;
    },
    setQueryMode(
      state,
      action: PayloadAction<{ type: string; currentTabIndex: number }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).queryMode =
        action.payload.type;
    },
    setQuerySubType(state, action) {
      getCurrentQuery(state, action.payload.currentTabIndex).querySubType =
        action.payload.type;
    },
    setMsrSelections(
      state,
      action: PayloadAction<{ msr: string[]; currentTabIndex: number }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).msrSelections =
        action.payload.msr;
    },

    setIsQueryUpdated(
      state,
      action: PayloadAction<{ flag: boolean; currentTabIndex: number }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).isQueryUpdated =
        action.payload.flag;
    },
    setIsUpdatedTab(
      state,
      action: PayloadAction<{ flag: boolean; currentTabIndex: number }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).isUpdatedTab =
        action.payload.flag;
    },
    setIsSavedQueryLoaded(
      state,
      action: PayloadAction<{ flag: boolean; currentTabIndex: number }>,
    ) {
      getCurrentQuery(
        state,
        action.payload.currentTabIndex,
      ).isSavedQueryLoaded = action.payload.flag;
    },
    setIsLoadingQuery(
      state,
      action: PayloadAction<{ flag: boolean; currentTabIndex: number }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).isLoadingQuery =
        action.payload.flag;
    },
    // setIsSavedQueryLoaded(state,action:PayloadAction<{flag:boolean, currentTabIndex:number}>) {
    //   getCurrentQuery(state,action.payload.currentTabIndex).isSavedQueryLoaded= action.payload.flag
    // },
    setResultHeaders(
      state,
      action: PayloadAction<{ headers: any[]; currentTabIndex: number }>,
    ) {
      if (getCurrentQuery(state, action.payload.currentTabIndex).results) {
        getCurrentQuery(
          state,
          action.payload.currentTabIndex,
        ).results.result.headers = action.payload.headers;
      }
    },
    setResults(state, action) {
      getCurrentQuery(state, action.payload.currentTabIndex).results =
        action.payload.result;
    },
    setLoading(
      state,
      action: PayloadAction<{ flag: boolean; currentTabIndex: number }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).loading =
        action.payload.flag;
    },

    addNewTab(state, action) {
      state.queries.push({
        ...singleQueryState,
        tabName: action.payload
          ? action.payload
          : getNextTabName(state.queries , false),
        isSavedQueryLoaded: action.payload ? false : true,
        tabOrder: state.queries.length + 1,
      });
      if (!action.payload) {
        state.currentTabIndex = state.queries.length - 1;
      }
    },

    cloneTab(state, action) {
      state.queries.push({
        ...state.queries[state.currentTabIndex],
        currentQueryIndex: 0,
        historyCount: 0,
        tabName: getNextTabName(state.queries, false),
        tabOrder: state.queries.length + 1,
      });
      if (!action.payload) {
        state.currentTabIndex = state.queries.length - 1;
      }
    },
    changeTab(state, action: PayloadAction<number>) {
      state.currentTabIndex = action.payload;
    },
    removeTab(state, action: PayloadAction<number>) {
      state.queries.splice(action.payload, 1);
      state.currentTabIndex = Math.max(0, state.currentTabIndex - 1);
    },
    setLoadQueryIds(state, action: PayloadAction<any[]>) {
      state.loadQueryIds = action.payload;
    },
    setSavingQuery(state, action) {
      getCurrentQuery(state, action.payload.currentTabIndex).isSavingQuery =
        action.payload.flag;
    },
    changeTabName(
      state,
      action: PayloadAction<{ tabName: string; index: number }>,
    ) {
      state.queries[action.payload.index].tabName = action.payload.tabName;
    },
    resetTabname(
      state,
      action: PayloadAction<{  index: number }>,
    ) {
      state.queries[action.payload.index].tabName = getNextTabName(state.queries, true, action.payload.index );
    },
    resetQuerySlice(state) {
      state.queries = [{ ...singleQueryState }];
      state.currentTabIndex = 0;
    },
    setAllTabUpdated(state) {
      state.queries = state.queries.map((item: QueryState) => ({
        ...item,
        isUpdatedTab: false,
      }));
    },

    setIsLoadingSavedQueries(state, action: PayloadAction<boolean>) {
      state.isLoadingSavedQueries = action.payload;
    },
    setCurrUpdatingTabIndex(state, action: PayloadAction<number>) {
      state.currUpdatingTabIndex = action.payload;
    },
    setUpdatedNamedsetList(
      state,
      action: PayloadAction<{
        currentTabIndex: number;
        updatedNamedsetList: Record<string, string[]>;
      }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).updatedNamedsetList =
        action.payload.updatedNamedsetList;
    },

    resetUpdatedNamedsetList(
      state,
      action: PayloadAction<{
        currentTabIndex: number;
      }>,
    ) {
      getCurrentQuery(state, action.payload.currentTabIndex).updatedNamedsetList = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(newQuery.pending, (state, action) => {
        const { index } = action.meta.arg;

        getCurrentQuery(state, index).loading = true;
        getCurrentQuery(state, index).isNewQuery = true;
      })
      .addCase(newQuery.fulfilled, (state, action) => {
        const { index } = action.meta.arg;
        //state.loading = false;
        getCurrentQuery(state, index).query = action.payload;
        getCurrentQuery(state, index).error = null;
        getCurrentQuery(state, index).isNewQuery = false;
      })
      .addCase(newQuery.rejected, (state, action) => {
        const { index } = action.meta.arg;

        getCurrentQuery(state, index).loading = false;
        getCurrentQuery(state, index).error =
          "Error while creating new query. Please contact administrator.";
      })
      .addCase(setupQuery.pending, (state, action) => {
        const { index = null } = action.meta.arg;

        //  getCurrentQuery(state,index).loading = true;
      })

      .addCase(setupQuery.fulfilled, (state, action) => {
        const { index = null } = action.meta.arg;

        // getCurrentQuery(state,index).loading = false;
        if (getCurrentQuery(state, index).query) {
          getCurrentQuery(state, index).query = {
            ...getCurrentQuery(state, index).query,
            enhancedFilterCriteria: action.payload.enhancedFilterCriteria,
            axes: action.payload.axes,
          };
        }

        //state.error = null;
      })
      .addCase(setupQuery.rejected, (state, action) => {
        const { index = null } = action.meta.arg;

        getCurrentQuery(state, index).loading = false;
        getCurrentQuery(state, index).error =
          "Error while setting up the query. Please contact administrator.";
      })
      .addCase(resultQuery.pending, (state, action) => {
        const { index = null } = action.meta.arg;

        getCurrentQuery(state, index).loading = true;
        getCurrentQuery(state, index).error = null;
        getCurrentQuery(state, index).resetGrpFn = false;
      })
      .addCase(resultQuery.fulfilled, (state, action) => {
        const { index = null } = action.meta.arg;

        const q = getCurrentQuery(state, index);
        q.loading = false;
        // q.queryMode = action.payload.activeQueryDetail?.queryMode || "Grid";
        q.results = action.payload;
        q.isSourceHistory = false;
        q.resetGrpFn = true;
        q.currentQueryIndex = action.payload.activeIndex;
        q.historyCount = action.payload.history.length;
        q.updatedNamedsetList = {};
        q.currentQueryType =
          action.payload.activeQueryDetail?.queryMode || "Grid";
        if (action.payload.result.queryRuntime.cols > 200) {
          q.error = `Query result (with ${action.payload.result.queryRuntime.cols} columns) exceeds the maximum number of columns (200). Please refine your query.`;
          q.results = null;
          return;
        }
      })
      .addCase(resultQuery.rejected, (state, action) => {
        const { index = null } = action.meta.arg;

        getCurrentQuery(state, index).loading = false;
        getCurrentQuery(state, index).error =
          "Error while executing query. Please contact administrator.";
      })
      .addCase(historyQuery.pending, (state, action) => {
        const { currentTabIndex = null } = action.meta.arg;

        getCurrentQuery(state, currentTabIndex).loading = true;
        getCurrentQuery(state, currentTabIndex).error = null;
        getCurrentQuery(state, currentTabIndex).resetGrpFn = false;
      })
      .addCase(historyQuery.fulfilled, (state, action) => {
        const { currentTabIndex = null } = action.meta.arg;

        const q = getCurrentQuery(state, currentTabIndex);

        q.loading = false;
        q.queryMode = action.payload.activeQueryDetail?.queryMode || "Grid";
        if (
          action.payload.query.properties.subType &&
          action.payload.query.properties.subType != ""
        ) {
          q.querySubType = action.payload.query.properties.subType;
        }
        q.currentQueryType =
          action.payload.activeQueryDetail?.queryMode || "Grid";

        if (action.payload.activeQueryDetail?.queryMode != "Map") {
          q.results = action.payload;
        }
        q.resetGrpFn = true;
        q.isSourceHistory = true;
        q.currentQueryIndex = action.payload.activeIndex;
        q.historyCount = action.payload.history.length;

        if (q.query) {
          q.query = {
            ...q.query,
            enhancedFilterCriteria: action.payload.query.enhancedFilterCriteria,
            axes: action.payload.query.axes,
          };
        }
        if (action.payload.result.queryRuntime.cols > 200) {
          q.error = `Query result (with ${action.payload.result.queryRuntime.cols} columns) exceeds the maximum number of columns (200). Please refine your query.`;
          q.results = null;
          return;
        }
      })
      .addCase(historyQuery.rejected, (state, action) => {
        const { currentTabIndex = null } = action.meta.arg;

        getCurrentQuery(state, currentTabIndex).loading = false;
        getCurrentQuery(state, currentTabIndex).error =
          "Error while executing query. Please contact administrator.";
      })
      .addCase(groupFnQuery.pending, (state, action) => {
        const { index = null } = action.meta.arg;

        getCurrentQuery(state, index).loading = true;
        getCurrentQuery(state, index).error = null;
      })
      .addCase(groupFnQuery.fulfilled, (state, action) => {
        const { index = null } = action.meta.arg;

        getCurrentQuery(state, index).loading = false;
        getCurrentQuery(state, index).results = action.payload;
        if (action.payload.result.queryRuntime.cols > 200) {
          getCurrentQuery(state).error =
            `Query result (with ${action.payload.result.queryRuntime.cols} columns) exceeds the maximum number of columns (200). Please refine your query.`;
          getCurrentQuery(state).results = null;
          return;
        }
      })
      .addCase(groupFnQuery.rejected, (state, action) => {
        const { index = null } = action.meta.arg;

        getCurrentQuery(state, index).loading = false;
        getCurrentQuery(state, index).error =
          "Error while executing query. Please contact administrator.";
      });
  },
});

export const {
  setQuery,
  setQueryMode,
  setAxesWithAllSelected,
  setQuerySubType,
  setLoadQueryMode,
  setMsrSelections,
  setIsQueryUpdated,
  setResultHeaders,
  setLoading,
  setResults,
  setCurrentQueryIndex,
  setHistoryCount,
  setCurrentQueryType,
  setSourceFlag,
  addNewTab,
  removeTab,
  cloneTab,
  changeTab,
  setIsUpdatedTab,
  resetQuerySlice,
  setLoadQueryIds,
  setIsLoadingQuery,

  setIsLoadingSavedQueries,
  setAllTabUpdated,
  setSavingQuery,
  setCurrUpdatingTabIndex,
  setIsSavedQueryLoaded,
  changeTabName,
  resetTabname,
  setUpdatedNamedsetList,
  resetUpdatedNamedsetList
} = queryBuilderSlice.actions;
export default queryBuilderSlice.reducer;
